import { TableCell, TableRow } from '@mui/material';
import { ClientScore, ScoringFunction } from '../../../../types/advise';
import { TextBlock, TextSpan } from '../../../Atoms/Text';
import { TextButton } from '../../../Atoms/TextButton';
import { ClientAverageBar } from './ClientAverageBar';
import { RatingSlider } from './RatingSlider';
import { ScoreBadge } from './ScoreBadge';
import { ScoringPills } from './ScoringPills';
import { isAdjusted, isWideSpread, responsesLabel, ScoringView } from './SupplierScoring.calc';

// The client average beside the user's own rating.
export interface ClientRowScoring {
  view: ScoringView;
  clientScore?: ClientScore;
  detailsOpen: boolean;
  onToggleDetails: () => void;
}

interface FunctionRatingRowProps {
  fn: ScoringFunction;
  // Undefined when unrated, which is never the same as 0.
  rating?: number;
  // Left out for a reader, who sees the rating as a number.
  onRate?: (rating: number) => void;
  onRelease?: (rating: number) => void;
  // Held still while the rating is being sent, so nothing changes that the send leaves behind.
  disabled?: boolean;
  resetToken?: number;
  client?: ClientRowScoring;
}

/** One function's row: its 0–100 slider and score, or the client average in the "Client average" view. */
export const FunctionRatingRow = ({
  fn,
  rating,
  onRate,
  onRelease,
  disabled = false,
  resetToken,
  client
}: FunctionRatingRowProps) => {
  const rated = rating !== undefined;
  const clientScore = client?.clientScore;
  const showingClients = client?.view === 'client';
  return (
    <TableRow>
      <TableCell
        className="function-name"
        sx={{ pl: 3 }}>
        <span className="flex flex-wrap items-center gap-2">
          <TextSpan size="sm">{fn.label}</TextSpan>
          {client && (
            <ScoringPills
              adjusted={isAdjusted(rating, clientScore)}
              wideSpread={isWideSpread(clientScore)}
            />
          )}
        </span>
      </TableCell>
      <TableCell>
        {showingClients ? (
          <ClientAverageBar clientScore={clientScore} />
        ) : (
          <>
            {onRate ? (
              <RatingSlider
                label={fn.label}
                rating={rating}
                clientAverage={clientScore?.average}
                onRate={onRate}
                onRelease={onRelease}
                disabled={disabled}
                resetToken={resetToken}
              />
            ) : (
              <TextSpan
                size="sm"
                color={rated ? 'grey8' : 'grey6'}>
                {rated ? rating : '–'}
              </TextSpan>
            )}
            {client && (
              <TextBlock
                size="xs"
                color={clientScore ? 'purple' : 'grey6'}>
                {clientScore
                  ? `Client average ${clientScore.average} · ${responsesLabel(clientScore.scores.length)}`
                  : 'No client responses yet'}
              </TextBlock>
            )}
          </>
        )}
      </TableCell>
      <TableCell align="center">
        {showingClients ? (
          <ScoreBadge
            score={clientScore?.average ?? null}
            client
          />
        ) : (
          <ScoreBadge score={rated ? rating : null} />
        )}
      </TableCell>
      {client && (
        <TableCell align="right">
          <TextButton
            size="xs"
            onClick={client.onToggleDetails}>
            {client.detailsOpen ? 'Hide details' : 'Details ›'}
          </TextButton>
        </TableCell>
      )}
    </TableRow>
  );
};
